import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { AudioEngine } from './AudioEngine.js';

/**
 * Owns one AudioEngine for a screen and resolves clip ids to real URLs.
 *
 * The engine existed as 609 lines of well-tested module that NOTHING ever
 * instantiated: every component took an `audioEngine` prop defaulting to null,
 * so every `audioEngine && audioEngine.play(...)` guard quietly did nothing.
 * This hook is the missing wiring.
 *
 * @param {'host'|'player'} mode host plays at full gain; player auditions at 0.3
 */
export function useAudioEngine(mode = 'host') {
  const engineRef = useRef(null);
  const [ready, setReady] = useState(false);
  const clipUrlsRef = useRef(new Map());
  const inflightRef = useRef(new Map());

  if (!engineRef.current && typeof window !== 'undefined') {
    engineRef.current = new AudioEngine({ mode });
  }

  // Resolve clip id -> audio URL from the library manifest the Worker serves.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/library')
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (cancelled || !m?.clips) return;
        const map = new Map();
        for (const c of m.clips) {
          const url = c.formats?.opus || c.formats?.mp3 || c.url;
          if (c.id && url) map.set(c.id, url);
        }
        clipUrlsRef.current = map;
        setReady(true);
      })
      .catch(() => { /* leave empty; play() degrades to a no-op below */ });
    return () => { cancelled = true; };
  }, []);

  // Callers use two shapes: a full manifest clip ({ id, formats }) and the
  // engine's own item shape ({ clipId, gain }). Accept both — reading only
  // `clip.id` meant every playSequence([{ clipId }]) call resolved undefined
  // and silently played nothing, which is exactly how "no sound" looked.
  const idFor = (clip) => {
    if (!clip) return null;
    if (typeof clip === 'string') return clip;
    return clip.id || clip.clipId || null;
  };

  const urlFor = useCallback((clip) => {
    const id = idFor(clip);
    if (!id) return null;
    if (typeof clip === 'object' && clip) {
      const direct = clip.formats?.opus || clip.formats?.mp3 || clip.url;
      if (direct) return direct;
    }
    if (typeof id === 'string' && (id.startsWith('/') || id.startsWith('http') || id.startsWith('blob:') || id.startsWith('data:'))) {
      return id;
    }
    if (clipUrlsRef.current.has(id)) {
      return clipUrlsRef.current.get(id);
    }
    if (typeof id === 'string' && id.startsWith('sfx_')) {
      return `/audio/${id}.opus`;
    }
    return null;
  }, []);

  /** Preload once per clip; concurrent calls share one fetch. */
  const ensureLoaded = useCallback(async (clip) => {
    const engine = engineRef.current;
    const id = idFor(clip);
    if (!engine || !id) return false;
    if (engine.hasBuffer(id)) return true;

    if (inflightRef.current.has(id)) return inflightRef.current.get(id);

    const url = urlFor(clip);
    if (!url) return false;

    const p = engine.preload(id, url)
      .then(() => true)
      .catch((e) => {
        console.warn(`[audio] could not load "${id}" from ${url}:`, e.message);
        return false;
      })
      .finally(() => inflightRef.current.delete(id));

    inflightRef.current.set(id, p);
    return p;
  }, [urlFor]);

  const unlock = useCallback(async () => {
    if (engineRef.current) await engineRef.current.unlock();
  }, []);

  // Routed through playSequence even for a single clip, because that is the
  // only path that emits progress off the AUDIO clock (audioContext.currentTime,
  // every 20ms). Callers that draw a playhead were previously animating it from
  // Date.now(), started before the buffer began, so the cursor drifted away from
  // the sound it was supposed to be tracking.
  const play = useCallback(async (clip, options = {}) => {
    const engine = engineRef.current;
    if (!engine) return null;
    const ok = await ensureLoaded(clip);
    if (!ok) return null;
    const id = idFor(clip);
    if (options.onProgress || options.onEnded) {
      return engine.playSequence([{ clipId: id }], options);
    }
    return engine.playClip(id, options);
  }, [ensureLoaded]);

  const playSequence = useCallback(async (clips, options = {}) => {
    const engine = engineRef.current;
    if (!engine || !Array.isArray(clips) || clips.length === 0) return null;
    const loaded = await Promise.all(clips.map(ensureLoaded));
    const items = clips
      .map((c, i) => (loaded[i] ? { clipId: idFor(c), gapAfter: c?.gapAfter } : null))
      .filter(Boolean);
    if (items.length === 0) return null;
    return engine.playSequence(items, options);
  }, [ensureLoaded]);

  const audition = useCallback(async (clip, options = {}) => {
    const engine = engineRef.current;
    if (!engine) return null;
    const ok = await ensureLoaded(clip);
    if (!ok) return null;
    return engine.audition(idFor(clip), options);
  }, [ensureLoaded]);

  useEffect(() => () => {
    // Nothing to tear down beyond dropping the reference; the engine holds only
    // decoded buffers and an AudioContext the browser reclaims.
    engineRef.current = null;
  }, []);

  return useMemo(() => ({
    engine: engineRef.current,
    ready,
    unlock,
    play,
    playSequence,
    audition,
    preload: ensureLoaded,
  }), [ready, unlock, play, playSequence, audition, ensureLoaded]);
}

export default useAudioEngine;
